import { uuid } from "@xapp/chat-widget-core";
import { ChatUserInfo, WidgetConfigurableMessagesConfig, WidgetHooks } from "@xapp/stentor-chat-widget";
import { Response, UserProfile } from "stentor-models";
import { setAccountStatus, setConnectionStatus } from "../store/actions";
import { ACCOUNT_STATUS_TYPE, ActionType, CONNECTION_STATUS_TYPE } from "../store/ChatAction";
import { err, log, RetryRequest } from "../utils";
import { postMessageToStentor, StentorRequest } from "../utils/http-utils";
import { responseToMessage } from "../utils/responseToMessage";
import { ChatServer, ChatServerOptions, OfflineMessage, VisitorInfoMessage } from "./ChatServer";
import { ChatMessageRequest, ChatMessageRequestCustom, ChatServerMessage } from "./ChatServerMessage";
import { getConfigurableMessagesConfig } from "../utils/configurableMessages";
import setSessionId from "../store/actions/setSessionId";

const PERMISSION_QUESTION_EXPIRATION_MS = 300000; // 5 minutes

export interface StentorDirectChatConfig {
    readonly url: string;
    readonly key: string;
    readonly timeout?: number;
}

interface UserJoinMessage {
    readonly user: ChatUserInfo;
    readonly token: string; //todo: what is this
}

// interface UserLeaveMessage {
//     readonly user: ChatUserInfo;
//     readonly token: string; //todo: what is this
// }

export class StentorDirectChat implements ChatServer {
    private visitorInfo!: VisitorInfoMessage;

    private _userId = "";
    private _sessionId = "";
    private _accessToken = "";
    private _attributes: Record<string, unknown> = {};

    private dispatch!: (action: ActionType) => void;

    private isNewSession: boolean;

    private readonly configurableMessages: WidgetConfigurableMessagesConfig | undefined;
    private readonly config: StentorDirectChatConfig;
    private readonly options: ChatServerOptions | undefined;
    private readonly hooks?: WidgetHooks;

    constructor(config: StentorDirectChatConfig, options?: ChatServerOptions) {
        this.config = config;
        this.options = options;
        this.configurableMessages = options.configurableMessages;
        this.isNewSession = false;
        this.hooks = options.hooks;
    }

    public init(dispatch: (action: ActionType) => void): void {
        this.dispatch = dispatch;

        // TODO: Authenticate - or at least tell the server who you are
        log(`got init auth: ${this.config.key}`);

        // We are connected
        this.setConnectionStatus("online");
        this.setAccountStatus("online");
    };

    private setConnectionStatus(status: CONNECTION_STATUS_TYPE) {
        log(`SERVER: connection_update: ${JSON.stringify(status)}`);
        this.dispatch(setConnectionStatus(status));
    }

    private setAccountStatus(status: ACCOUNT_STATUS_TYPE) {
        log(`SERVER: account_status: ${JSON.stringify(status)}`);
        this.dispatch(setAccountStatus(status));
    }

    private sendNewMessage(data: ChatMessageRequest) {
        log(`SERVER: new message: ${JSON.stringify(data)}`);

        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.msg",
                user: {
                    ...data.user,
                    nick: "agent:robot",
                },
                msg: data.msg,
                timestamp: +new Date()
            }
        });
    }

    private userJoined(data: UserJoinMessage) {
        log(`SERVER: user joined: ${JSON.stringify(data)}`);

        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.memberjoin",
                user: {
                    ...data.user,
                    nick: "agent:robot",//todo: why change nick?
                },
                timestamp: +new Date(),
            }
        });
    }

    // private userLeft(data: UserLeaveMessage) {
    //     log(`SERVER: user left: ${JSON.stringify(data)}`);

    //     this.dispatch({
    //         type: "chat",
    //         detail: {
    //             type: "chat.memberleave",
    //             user: {
    //                 ...data.user,
    //                 nick: "agent:robot",//todo: why change nick?
    //             },
    //             timestamp: +new Date()
    //         }
    //     });
    // }

    private typing() {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.typing",
                user: {
                    nick: "agent:robot",
                },
                typing: true,
                timestamp: +new Date()
            }
        });
    }

    private stopTyping() {
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.typing",
                user: {
                    nick: "agent:robot",
                },
                typing: false,
                timestamp: +new Date()
            }
        });
    }

    private sendFailureMessage(retry: number, delay: number, text: string) {
        this.stopTyping();
        this.dispatch({
            type: "chat",
            detail: {
                type: "chat.failureMsg",
                user: {
                    nick: "agent:robot"
                },
                failureMsg: {
                    retry,
                    delay,
                    text,
                },
                timestamp: +new Date()
            }
        });
    }

    // private disconnect() {
    //     log("SERVER: disconnect");
    //     this.dispatch(setAccountStatus("offline"));
    //     this.dispatch(setConnectionStatus("offline"));
    // }

    // private reconnect() {
    //     log("SERVER: reconnect");
    //     this.dispatch(setAccountStatus("online"));
    // }

    // private reconnectError() {
    //     log("SERVER: reconnect error");
    //     this.dispatch(setAccountStatus("offline"));
    // }

    public sendOfflineMsg(_: OfflineMessage, cb: (error?: Error) => void): void {
        cb();
    }

    public async sendChatMsg(message: ChatServerMessage, cb: (err?: Error) => void): Promise<void> {
        return this.sendChatMsgRequest({
            msg: message,
            timestamp: new Date().getTime(),
            agent: false,
            user: undefined,
            type: "msg"
        }, cb);
    }

    public async bargeOut(_cb: (err?: Error) => void): Promise<void> {
        throw new Error("Method not implemented: bargeOut");
    }

    public async bargeIn(_agentName: string, _cb: (err?: Error) => void): Promise<void> {
        throw new Error("Method not implemented: bargeIn");
    }

    private getBot(user: ChatUserInfo | undefined): ChatUserInfo {
        const bot = this.options?.bot;
        if (!user) {
            return bot;
        }
        if (user.displayName && user.nick && user.avatarPath) {
            return user;
        }
        return {
            ...user,
            nick: user.nick || bot.nick || "Bot",
            displayName: user.displayName || bot.displayName || "Bot",
            avatarPath: user.avatarPath || bot.avatarPath
        };
    }

    public async sendChatMsgRequest(serviceRequest: ChatMessageRequest, cb: (err?: Error) => void): Promise<void> {
        let agentResponse = await this.postMessage(serviceRequest);
        if (!agentResponse.user?.displayName) {
            agentResponse = {
                ...agentResponse,
                user: this.getBot(agentResponse.user)
            }
        }

        this.sendNewMessage(agentResponse);

        cb();
    }

    public sendTyping(): void {
    }

    public setVisitorInfo(visitorInfo: VisitorInfoMessage, sessionId: string, cb: (error?: Error) => void): void {
        this.visitorInfo = visitorInfo;

        this._attributes = { ...this.visitorInfo.attributes };

        // do not set currentUrl if localhost, localhost is not liked by some firewalls, like AWS WAF
        const href = new URL(window.location.href);

        if (href?.host.toLowerCase().startsWith(`localhost`)) {
            // we still need to communicate it is localhost though so we make this boolean
            this._attributes.isLocal = true;
        } else {
            // not localhost, set it
            this._attributes.currentUrl = window.location.href
        }

        this._accessToken = this.visitorInfo.accessToken;

        // This is for the bot
        this.userJoined({
            user: this.getBot(undefined),
            token: ""
        });

        this.startSession(sessionId);

        cb();
    }

    public sendChatRating(): void {
    }

    public sendFile(_: File, cb: (err?: Error) => void): void {
        cb();
    }

    public markAsRead(): void { // mark the messages read
    }

    public flush(): void {
    }

    public dispose(): void {
    }

    public sleep(): void {
    }

    public wakeup(): void {
    }

    private async postMessage(message: ChatMessageRequest): Promise<ChatMessageRequest> {
        let request: StentorRequest;

        const userId: string = this._userId;
        const sessionId: string = this._sessionId;
        const accessToken: string = this._accessToken;
        const attributes: Record<string, unknown> = this._attributes || {};

        // First translate the request to bot dialect

        const now = new Date().getTime();

        if (this.isNewSession && !message?.msg?.text) {
            request = {
                type: "LAUNCH_REQUEST",
                rawQuery: message?.msg?.text,
                sessionId,
                userId,
                isNewSession: true,
                intentId: "LaunchRequest",
                platform: "stentor-platform",
                channel: "widget",
                accessToken,
                attributes
            };
        } else {
            // Pending permission request?

            const permissionRequest = message.msg?.permissionRequest;

            if (permissionRequest) {
                message = {
                    ...message,
                    msg: {
                        ...message.msg,
                        permissionRequest: undefined
                    }
                };
                const expired = now - permissionRequest.time > PERMISSION_QUESTION_EXPIRATION_MS;

                const text = message.msg.text?.toLowerCase();

                let granted = !expired;

                const userProfile: UserProfile = { id: this._userId };
                if (!expired) {
                    if (permissionRequest.type === "EMAIL") {
                        const isEmail = looksLikeEmail(text);
                        granted = isEmail;
                        userProfile.email = this.visitorInfo.email;
                        userProfile.name = this.visitorInfo.displayName;
                        this.visitorInfo.email = text;
                    }
                    if (permissionRequest.type === "LOCATION_PRECISE") {
                        granted = !!message.msg.location;
                        userProfile.preciseLocation = message.msg.location ? {
                            coordinates: {
                                latitude: message.msg.location.latitude,
                                longitude: message.msg.location.longitude
                            }
                        } as undefined : undefined;
                    }
                }

                request = {
                    type: "PERMISSION_GRANT",
                    rawQuery: message.msg.text,
                    sessionId: this._sessionId,
                    userId: this._userId,
                    isNewSession: false,
                    granted,
                    userProfile,
                    intentId: "PermissionGrant",
                    platform: "stentor-platform",
                    channel: "widget",
                    accessToken,
                    attributes
                };

            } else if (message.type === "custom") {
                request = {
                    ...JSON.parse((message as ChatMessageRequestCustom).payload),
                    // token: message.msg.token,
                    sessionId: this._sessionId,
                    userId: this._userId,
                    isNewSession: false,
                    // intentId: "OptionSelect",
                    platform: "stentor-platform",
                    channel: "widget",
                    accessToken,
                    attributes
                };
            } else if (message.msg.token) {
                request = {
                    type: "OPTION_SELECT_REQUEST",
                    token: message.msg.token,
                    sessionId: this._sessionId,
                    userId: this._userId,
                    isNewSession: false,
                    intentId: "OptionSelect",
                    platform: "stentor-platform",
                    channel: "widget",
                    accessToken,
                    attributes
                };
            } else {
                request = {
                    type: "INTENT_REQUEST",
                    rawQuery: message.msg.text,
                    sessionId: this._sessionId,
                    userId: this._userId,
                    isNewSession: false,
                    intentId: "NLU_RESULT_PLACEHOLDER",
                    platform: "stentor-platform",
                    channel: "widget",
                    accessToken,
                    attributes
                };
            }
        }

        log(`Visitor says: ${JSON.stringify(request, undefined, 2)}`);

        const configurableMessages = getConfigurableMessagesConfig(this.configurableMessages);

        // Call bot
        let botResponse: Response;
        let successResult = false;

        const success = (result: Response) => {
            botResponse = result;
            successResult = true;
        }

        const fail = async (i: number, error: Error): Promise<void> => {
            const retry = configurableMessages[i].retry;
            const delay = configurableMessages[i].delay;
            const text = configurableMessages[i].text;
            err(`POST failed: ${error}`);
            this.sendFailureMessage(retry, delay, text);
            botResponse = { errorText: "" } as unknown;
            // We need to delay before we actually make another fetch call again
            return new Promise((resolve) => {
                // * 1000 to make it seconds
                setTimeout(resolve, delay * 1000);
            });
        }

        for (let i = 1; i < configurableMessages.length; i++) {
            // Right now the timeout and delay are the same.  Maybe we increase the timeout or make that constant?
            const timeout = this.config.timeout || 15000; // configurableMessages[i - 1].delay * 1000;

            this.typing();
            // We don't start typing IF we have a failure message displayed and we are waiting on the timeout
            await new RetryRequest({ retries: 1, timeout })
                .fetch((signal: AbortSignal) => {
                    return postMessageToStentor(request, this.config.url, this.config.key, signal)
                })
                .then(success)
                // bind the i, the current iteration, to the fail function for looking up the message
                .catch(fail.bind(this, i));

            // If we have what we want, break the for loop
            if (successResult) {
                break;
            }
        };

        log(`Bot says: ${JSON.stringify(botResponse, undefined, 2)}`);

        // Now translate the response to server dialect

        // new session no more
        this.isNewSession = false;

        let responseMessage: ChatMessageRequest = responseToMessage(botResponse);

        if (botResponse?.hasOwnProperty("errorText") || !botResponse) {
            responseMessage = {
                type: "userLeft",
                timestamp: now,
                msg: {},
                user: undefined,
                endSession: true
            };

            responseMessage.msg = {
                //eslint-disable-next-line
                //@ts-ignore
                text: botResponse?.hasOwnProperty("errorText") ? botResponse.errorText : "The assistant is not responding right now.  Please close the widget and open it again."
            };
        }

        if (typeof this.hooks?.onResponse === "function") {
            this.hooks.onResponse(botResponse);
        }

        this.stopTyping();

        return responseMessage;
    }

    private startSession(sessionId: string): void {
        if (this.visitorInfo.visitorId) {
            this._userId = `${this.visitorInfo.visitorId}`;
        } else if (this.visitorInfo.email) {
            this._userId = `stentor-widget-user-${this.visitorInfo.email}`;
        } else {
            this._userId = `stentor-widget-user-${uuid()}`;
        }

        if (sessionId) {
            this._sessionId = sessionId;
            log(`Using persisted session id: ${this._sessionId}`);
        } else {
            this._sessionId = `stentor-widget-session-${uuid()}`;
            this.dispatch(setSessionId(this._sessionId));
            log(`Using generated session id: ${this._sessionId}`);
        }

        // This is a flag that is cleared after the first message is sent
        this.isNewSession = true;
    }

    // private restartSession(): void {
    //     this._sessionId = `stentor-chat-session-${uuid()}`;
    //     this.isNewSession = true;
    // }

    // private endSession(): Promise<void> {
    //     return Promise.resolve(undefined);
    // }

    get userId(): string {
        return this._userId;
    }

    get sessionId(): string {
        return this._sessionId;
    }
}

function looksLikeEmail(email: string) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}
